/**
 * A v4 UUID that works on every browser the site is served to.
 *
 * `crypto.randomUUID()` exists only from Chrome 92 / Safari 15.4 / Firefox 95
 * (mid-2021). The site's build target is Chrome 87, so a phone on Chrome
 * 87–91 parses the bundle fine and then throws `TypeError: crypto.randomUUID
 * is not a function` the first time a page mints an event id. Measured
 * 2026-09-25: the owner's Xiaomi Mi 8 Lite (Android 9, Chrome 89) rendered
 * `/plans` for a moment and then hit the route error boundary («Страница не
 * открылась») — the ViewContent effect on mount was the caller. Over 14 days
 * of nginx, Chrome < 92 was 0.5% of `/plans` visitors, so the crash was rare
 * but total for the people it hit.
 *
 * Every event/checkout id in the app goes through here. The fallback uses
 * `crypto.getRandomValues` (Chrome 11+), and only if even that is missing —
 * a non-secure context or a very old WebView — degrades to `Math.random`,
 * which is fine for an analytics dedup id and is never used for security.
 */
export function uuid(): string {
  const c: Crypto | undefined = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // RFC 4122 §4.4: version 4 in the high nibble of byte 6, variant 10xx in byte 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
